<template>

    <!-- Range navigation spans the x-axis (years) -->
    <RangeNavigation
        :timelineWidth="width"
        :yBarExpanded="yBarExpanded"
        :show="showRangeNavigation && priority.selected"
        :zoomControlEnabled="showZoomControl"
        @navigate="NavigateTimeline($event)"
        @hide="ShowRangeNavigation(false)"
    />

    <div id="content" :style="`left: ${timelinePosition}%;`" tabindex="0">
        <div id="headers" class="flex" :style="`width: ${width};`">
            <Header
                v-for="(priority, p) in priorityTypes"
                :key="p"
                :priority="priority"
                :width="PriorityWidth(priority)"
            />
        </div>

        <!-- Controls zoom level of timeline (scaling) -->
        <ZoomControl
            :styles="ZoomControlStyles()"
            :topography="totalDays + 10"
            @zoom="Zoom($event)"
        />

        <div id="x-bar" class="flex" :style="`width: ${width};`" @mouseenter="ShowRangeNavigation(true)">
            <YearLabel
                v-for="(year, y) in years"
                :key="y"
                :year="year"
                :priority="priority"
                :showIconInformation="showRangeNavigation"
                :maxTopography="MaxTopography()"
                :timelineData="timelineData"
                :index="y"
                :enabled="enableYearLabelButtons"
                :selectedYearRange="selectedProject.yearRange"
                @project="$emit('project', $event)"
            />
        </div>
        <div id="data" :style="`width: ${width};`" v-if="priority && priority.selected">
            <Project
                v-for="(project, p) in timelineData"
                :key="p"
                :coordinates="project.TimelineCoordinates"
                :totalYears="total"
                :project="project"
                :index="p"
                :timelineZoom="timelineZoom"
                :projectYears="projectYears"
                :focusedYear="focusedProject.year"
                :selectedProject="selectedProject"
                :searching="searching"
                @hover="ProjectHover($event)"
                @project="$emit('project', $event)"
            />
            <Month
                v-for="(_, m) in 12"
                :key="m"
                :years="years"
                :priority="priority"
            />
        </div>

        <!-- Highlights selection of years categorized by priority once selected -->
        <PrioritySelectionIndicator
            v-for="(priorityType, p) in priorityTypes"
            :key="p"
            :id="`priority-selection-indicator-${p}`"
            :priorityType="priorityType"
            :width="PriorityWidth(priorityType)"
            :left="priorityWidths[p - 1]"
            :selected="priority.type"
            :noUpdate="zooming"
        />

    </div>
</template>

<script>
import YearLabel from "@/components/Timeline/Years/Label.vue";
import Month from "@/components/Timeline/Month.vue";
import Header from "@/components/Timeline/Years/Header.vue";
import PrioritySelectionIndicator from "@/components/Timeline/Years/PrioritySelectionIndicator.vue";
import Project from "@/components/Timeline/Project/Project.vue";
import RangeNavigation from "@/components/Timeline/Navigation/Range/Navigation.vue";
import ZoomControl from "@/components/Timeline/ZoomControl/Control.vue";
import PriorityTypes from "@/assets/JS/Data/Navigation.json";
import { CONFIG } from "@/assets/JS/Config.js";
import { ACCESSIBILITY } from "@/assets/JS/Assets.js";
import $ from "jquery";

export default {
    name: "TimelineContent",
    props: {
        years: Array,
        width: String,
        distance: String,
        total: Number,
        priority: Object,
        timelineData: Array,
        yBarExpanded: Boolean,
        timelineZoom: Number,
        showZoomControl: Boolean,
        totalDays: Number,
        selectedProject: Object,
        enableYearLabelButtons: Boolean,
        searching: Boolean
    },
    components: {
        YearLabel,
        Month,
        Header,
        PrioritySelectionIndicator,
        Project,
        RangeNavigation,
        ZoomControl
    },
    data: () => ({
        priorityTypes: PriorityTypes.Projects.PriorityTypes,
        priorityWidths: [],
        timelinePosition: "0",
        showRangeNavigation: false,
        zooming: false,
        focusedProject: {
            projectID: 0,
            index: 0,
            year: 0
        },
        projectYears: {
            socialImpactStartYear: 0,
            list: []
        }
    }),
    methods: {
        PriorityWidth: function(priority) {
            let width = this.TimelineWidth();
            const yearWidth = width / this.total;
            width = `${yearWidth * priority.TotalYears}%`;
            this.priorityWidths.push(width);
            return width;
        },
        TimelineWidth: function() {
            return Number(this.width.replace("%", ""));
        },
        NavigateTimeline: function(position) {
            if (position <= 0) {
                position = 0;
            }
            this.timelinePosition = `-${position}`;
        },
        ShowRangeNavigation: function(show) { this.showRangeNavigation = show; },
        ZoomControlStyles: function() {
            return {
                opacity: this.showZoomControl ? 1 : 0,
                bottom: this.showZoomControl ? 0 : -CONFIG.timeline.zoom.scrubber.height
            };
        },
        Zoom: function($event) {
            this.$emit("zoom", $event);
            this.zooming = true;
            this.timelinePosition = "0";
        },
        ProjectHover: function(projectIDs) { this.focusedProject = projectIDs; },

        // Gets the max topography (z-index) from project maps
        MaxTopography: function() {
            const maxTopography = Math.max.apply(
                null, 
                $.map($(".project-detail"), function(e, n) {
                    if ($(e).css("position") !== "static") {
                        return parseInt($(e).css("z-index")) || 1;
                    }
                })
            );
            if ($(".project-detail").length) {
                this.$emit("max-topography", maxTopography);
                return maxTopography;
            }
            return 0;
        }

    },
    beforeMount: function() {
        for (const year of this.years) {
            this.projectYears.list.push(year.year);
        }
        const prioritySocialImpact = this.priorityTypes.filter(p => p.Name === "Social Impact")[0];
        const priorityArchived = this.priorityTypes.filter(p => p.Name === "Experience Archive")[0];
        this.projectYears.socialImpactStartYear = prioritySocialImpact.DateRange.Start;

        // Add project counts for priority types
        const { counts } = this.priority;
        if (counts) {
            const { TotalSocialImpactProjects, TotalArchivedProjects } = counts;
            prioritySocialImpact.TotalProjects = TotalSocialImpactProjects;
            priorityArchived.TotalProjects = TotalArchivedProjects;
        }

    },
    mounted: function() {
        this.$emit("max-topography", this.MaxTopography());
        const component = this;
        ACCESSIBILITY.TimelineNavigation("content", function(key) {
            const timelineWidth = component.TimelineWidth();
            const xItemWidth = timelineWidth / component.total;
            switch(key) {
                case "ArrowRight":
                    if (Math.abs(component.timelinePosition) >= Math.abs(Math.floor(timelineWidth)) - (100 + xItemWidth)) {
                        component.timelinePosition = -(Math.floor(timelineWidth) - 100);
                    } else {
                        component.timelinePosition -= xItemWidth;
                    }
                    break;
                case "ArrowLeft":
                    if (component.timelinePosition >= (0 - xItemWidth)) {
                        component.timelinePosition = 0;
                    } else {
                        component.timelinePosition += xItemWidth;
                    }
                    break;
            }
        });
    },
    emits: [
        "zoom",
        "project",
        "max-topography"
    ]
};
</script>